import { useContext, useEffect, useMemo, useState } from 'react'

import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'

import { FeedbackContext } from '~/components/feedback'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'

import { getApiErrorCode } from '../helpers/advertiserBrandErrors'
import { identityKey, ParsedProductCodes } from '../helpers/gtin'
import { settleBatch } from '../helpers/settleBatch'
import {
  addAdvertiserBrandProductsService,
  advertiserBrandCatalogSearchService,
  createAdvertiserBrandService,
  deleteAdvertiserBrandProductsService,
  listAdvertiserBrandProductsService,
  moveAdvertiserBrandProductsService
} from '../services/advertiser-brand'
import {
  advertiserBrandCatalogBatchQueryKey,
  advertiserBrandCatalogNameSearchQueryKey,
  advertiserBrandCatalogSearchQueryKey,
  advertiserBrandKey,
  advertiserBrandProductsQueryKey,
  invalidateAdvertiserBrandQueries
} from './advertiserBrandQueryKeys'
import { useAdvertiserBrandErrorMessage } from './useAdvertiserBrands'

/** Espera de digitação antes de buscar por nome no catálogo. */
const NAME_SEARCH_DEBOUNCE = 400
/** Abaixo disso a busca por nome devolve resultado demais para valer a chamada. */
export const NAME_SEARCH_MIN_LENGTH = 3
const NAME_SEARCH_QUANTITY = 25

export const useAdvertiserBrandProducts = (
  params: ListAdvertiserBrandProductsRequest,
  { enabled = true }: { enabled?: boolean } = {}
) => {
  const { isPending, isFetching, data, error } = useQuery({
    queryKey: advertiserBrandKey(advertiserBrandProductsQueryKey, params),
    enabled,
    queryFn: async () => {
      const response = await listAdvertiserBrandProductsService({
        ...params,
        count: true
      })

      return response.data
    },
    placeholderData: previousData => previousData
  })

  return { isPending, isFetching, data, error }
}

type CatalogLookup = { publisherId: string | null; codes: string[] }

/**
 * Agrupa as linhas da página: uma consulta para os EANs (todos os publishers)
 * e uma por publisher para os SKUs, que só valem no publisher da linha.
 */
const toCatalogLookups = (products: AdvertiserBrandProduct[]) => {
  const gtins: string[] = []
  const skusByPublisher: Record<string, string[]> = {}

  products.forEach(product => {
    if (product.gtin) {
      gtins.push(product.gtin)

      return
    }

    if (!product.sku || !product.publisher_id) return

    skusByPublisher[product.publisher_id] = [
      ...(skusByPublisher[product.publisher_id] ?? []),
      product.sku
    ]
  })

  const lookups: CatalogLookup[] = Object.entries(skusByPublisher).map(
    ([publisherId, codes]) => ({ publisherId, codes })
  )

  return gtins.length
    ? [{ publisherId: null, codes: gtins }, ...lookups]
    : lookups
}

/**
 * Nome e imagem frescos do catálogo para as linhas da página. Produto sem
 * correspondência, ou cuja consulta falhou, não volta: o front trata como
 * hint, não erro.
 */
export const useAdvertiserBrandCatalogSearch = (
  products: AdvertiserBrandProduct[]
) => {
  const lookups = useMemo(() => toCatalogLookups(products), [products])

  return useQueries({
    queries: lookups.map(({ publisherId, codes }) => ({
      queryKey: advertiserBrandKey(
        advertiserBrandCatalogSearchQueryKey,
        publisherId,
        codes
      ),
      staleTime: 5 * 60 * 1000,
      queryFn: async () => {
        const response = await advertiserBrandCatalogSearchService(
          publisherId
            ? { product_skus: codes, publisher_id: publisherId }
            : { product_gtins: codes }
        )

        return response.data
      }
    })),
    combine: results => ({
      catalogByKey: results.reduce<
        Record<string, AdvertiserBrandCatalogSearchItem>
      >((acc, { data }, index) => {
        const { publisherId } = lookups[index]

        data?.forEach(item => {
          // na consulta por SKU, a linha é do publisher consultado, mesmo que
          // o catálogo já devolva o item com EAN
          let key = item.gtin

          if (publisherId) {
            key = identityKey({
              gtin: null,
              sku: item.product_sku,
              publisherId
            })
          }

          if (key) acc[key] = item
        })

        return acc
      }, {}),
      isFetching: results.some(result => result.isFetching)
    })
  })
}

/** Busca por nome no catálogo dos publishers, com debounce e mínimo de caracteres. */
export const useAdvertiserBrandCatalogNameSearch = (
  term: string,
  { enabled = true }: { enabled?: boolean } = {}
) => {
  const trimmed = term.trim()
  const [debouncedTerm, setDebouncedTerm] = useState(trimmed)

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedTerm(trimmed),
      NAME_SEARCH_DEBOUNCE
    )

    return () => clearTimeout(timer)
  }, [trimmed])

  const isActive = trimmed.length >= NAME_SEARCH_MIN_LENGTH

  const { data, isFetching } = useQuery({
    queryKey: advertiserBrandKey(
      advertiserBrandCatalogNameSearchQueryKey,
      debouncedTerm
    ),
    enabled: enabled && isActive && debouncedTerm === trimmed,
    queryFn: async () => {
      const response = await advertiserBrandCatalogSearchService({
        query: debouncedTerm,
        identifier_types: ['ean', 'sku'],
        quantity: NAME_SEARCH_QUANTITY
      })

      return response.data
    }
  })

  // enquanto o debounce não fecha, o termo antigo não vale como resultado
  const isSettled = debouncedTerm === trimmed

  return {
    isActive,
    items: isActive && isSettled ? (data ?? []) : [],
    isFetching: isActive && (isFetching || !isSettled)
  }
}

/**
 * Busca em lote por EAN e por SKU, disparada sob demanda: editar a lista troca
 * a chave e descarta o resultado anterior, para não listar produto de outra
 * colagem. O BFF não combina `product_gtins` com `product_skus`, então são
 * duas buscas em paralelo, e o produto que as duas devolvem entra uma vez só.
 */
export const useAdvertiserBrandCatalogBatchSearch = ({
  gtins,
  skus
}: Pick<ParsedProductCodes, 'gtins' | 'skus'>) => {
  const { t } = useAPPTranslation()
  const { toast } = useContext(FeedbackContext)
  const { data, isFetching, refetch } = useQuery({
    queryKey: advertiserBrandKey(
      advertiserBrandCatalogBatchQueryKey,
      gtins,
      skus
    ),
    enabled: false,
    queryFn: async () => {
      // uma busca que falha não descarta o que a outra achou; o aviso diz que a lista está incompleta
      const settled = await Promise.allSettled([
        gtins.length
          ? advertiserBrandCatalogSearchService({ product_gtins: gtins })
          : null,
        skus.length
          ? advertiserBrandCatalogSearchService({ product_skus: skus })
          : null
      ])
      const failed = settled.filter(
        (result): result is PromiseRejectedResult =>
          result.status === 'rejected'
      )

      // sem nenhuma busca que tenha dado certo, o erro sobe como antes
      if (
        failed.length &&
        settled.every(result => result.status === 'rejected' || !result.value)
      )
        throw failed[0].reason

      if (failed.length) toast.critical(t('c:messages.errorOccurred.message'))

      const [byGtin, bySku] = settled.map(result =>
        result.status === 'fulfilled' ? result.value : null
      )

      const unique = new Map<string, AdvertiserBrandCatalogSearchItem>()
      // o produto repetido some da lista, mas o código dele já foi achado
      const matched = new Set<string>()
      // códigos com que cada produto foi achado: o SKU colado pode vir de outra busca que não a do item mantido
      const aliases: Record<string, string[]> = {}

      ;[...(byGtin?.data ?? []), ...(bySku?.data ?? [])].forEach(item => {
        if (item.gtin) matched.add(item.gtin)
        if (item.product_sku) matched.add(item.product_sku)

        const key = identityKey({
          gtin: item.gtin,
          sku: item.product_sku,
          publisherId: item.publisher?.id ?? null
        })

        aliases[key] = [
          ...(aliases[key] ?? []),
          ...[item.gtin, item.product_sku].filter(Boolean)
        ] as string[]

        if (!unique.has(key)) unique.set(key, item)
      })

      return { items: [...unique.values()], matched: [...matched], aliases }
    }
  })

  return {
    items: data?.items,
    /** Códigos colados que o catálogo reconheceu, inclusive nos produtos repetidos. */
    matched: data?.matched,
    /** Chave do produto → códigos com que ele foi achado, nas duas buscas. */
    aliases: data?.aliases,
    isFetching,
    // `refetch` não rejeita: sem olhar `isError`, uma falha de rede viraria
    // "nenhum encontrado" em silêncio
    search: async () => {
      const result = await refetch()

      if (result.isError) {
        toast.critical(t('c:messages.errorOccurred.message'))

        return []
      }

      return result.data?.items ?? []
    }
  }
}

const sum = (values: number[]) =>
  values.reduce((total, value) => total + value, 0)

export const useAdvertiserBrandProductMutations = () => {
  const { t } = useAPPTranslation()
  const { toast } = useContext(FeedbackContext)
  const queryClient = useQueryClient()
  const getErrorMessage = useAdvertiserBrandErrorMessage()

  const invalidate = () => invalidateAdvertiserBrandQueries(queryClient)

  const notifyAdded = (
    results: AddAdvertiserBrandProductsResponse[],
    requests: AddAdvertiserBrandProductsRequest[]
  ) => {
    const created = sum(results.map(result => result.created))
    const ignored = sum(results.map(result => result.ignored))

    if (requests.length === 1) {
      const key = requests[0].brand
        ? 'rm:advertiserProducts.add.success'
        : 'rm:advertiserProducts.add.successUnassigned'

      toast.success(t(key, { count: created, brand: requests[0].brand }))
    } else {
      toast.success(
        t('rm:advertiserProducts.add.successMany', { count: created })
      )
    }

    if (ignored) {
      toast.informational(
        t('rm:advertiserProducts.add.ignored', { count: ignored })
      )
    }
  }

  const notifyMoved = (results: MoveAdvertiserBrandProductsResponse[]) => {
    const updated = sum(results.map(result => result.updated))
    const brands = new Set(results.map(result => result.brand))

    if (brands.size === 1) {
      toast.success(
        t('rm:advertiserProducts.linkBrand.success', {
          count: updated,
          brand: results[0].brand
        })
      )

      return
    }

    toast.success(
      t('rm:advertiserProducts.linkBrand.successMany', { count: updated })
    )
  }

  const addProducts = useMutation({
    mutationFn: async (body: AddAdvertiserBrandProductsRequest) => {
      const response = await addAdvertiserBrandProductsService(body)

      return response.data
    },
    onSuccess: (data, variables) => {
      invalidate()
      notifyAdded([data], [variables])
    },
    onError: error => toast.critical(getErrorMessage(error))
  })

  /**
   * Várias marcas de uma vez: requests independentes, um toast para o lote.
   * Marca nova nasce com os produtos, pela rota que cria a marca.
   */
  const addProductsBatch = useMutation({
    mutationFn: async (requests: AddAdvertiserBrandProductsBatchItem[]) => {
      const settled = await settleBatch(
        requests.map(async ({ isNewBrand, ...body }) => {
          if (isNewBrand && body.brand) {
            try {
              const created = await createAdvertiserBrandService({
                brand: body.brand,
                products: body.products
              })

              return created.data
            } catch (error) {
              // a lista de marcas é limitada, e outra linha pode ter criado a
              // mesma marca: se ela já existe, os produtos entram nela
              if (getApiErrorCode(error) !== 'BRAND_ALREADY_EXISTS') throw error
            }
          }

          return (await addAdvertiserBrandProductsService(body)).data
        })
      )

      return settled
    },
    onSuccess: ({ done, total, failed }, requests) => {
      if (failed) {
        toast.warning(
          t('rm:advertiserCatalog.batch.partial', { done: done.length, total })
        )

        return
      }

      notifyAdded(done, requests)
    },
    onError: error => toast.critical(getErrorMessage(error)),
    onSettled: invalidate
  })

  const moveProducts = useMutation({
    mutationFn: async (body: MoveAdvertiserBrandProductsRequest) => {
      const response = await moveAdvertiserBrandProductsService(body)

      return response.data
    },
    onSuccess: data => {
      invalidate()
      notifyMoved([data])
    },
    onError: error => toast.critical(getErrorMessage(error))
  })

  /** Mesmo lote independente do `addProductsBatch`: invalida dos dois lados. */
  const moveProductsBatch = useMutation({
    mutationFn: (requests: MoveAdvertiserBrandProductsRequest[]) =>
      settleBatch(
        requests.map(
          async body => (await moveAdvertiserBrandProductsService(body)).data
        )
      ),
    onSuccess: ({ done, total, failed }) => {
      if (failed) {
        toast.warning(
          t('rm:advertiserCatalog.batch.partial', { done: done.length, total })
        )

        return
      }

      notifyMoved(done)
    },
    onError: error => toast.critical(getErrorMessage(error)),
    onSettled: invalidate
  })

  const removeProducts = useMutation({
    mutationFn: async (body: DeleteAdvertiserBrandProductsRequest) => {
      const response = await deleteAdvertiserBrandProductsService(body)

      return response.data
    },
    onSuccess: data => {
      invalidate()
      toast.success(
        t('rm:advertiserProducts.remove.success', { count: data.deleted })
      )
    },
    onError: error => toast.critical(getErrorMessage(error))
  })

  return {
    addProducts,
    addProductsBatch,
    moveProducts,
    moveProductsBatch,
    removeProducts
  }
}
